import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Globe2, Import, Pencil, Trash2, Rocket, Globe, CopyX,
  FileText, ExternalLink, RefreshCw, Info, Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useSite } from "@/context/SiteContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const SourceChip = ({ source }) => {
  const map = {
    scraped: "bg-sky-500/10 text-sky-700 border-sky-500/30",
    manual: "bg-black/[0.05] text-[#6e6e6e] border-black/10",
    compiled: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    generated: "bg-violet-500/10 text-violet-700 border-violet-500/30",
  };
  return (
    <span className={`rounded-[5px] border px-1.5 py-0.5 font-nunito text-[8.5px] font-extrabold uppercase tracking-[0.1em] ${map[source] || map.manual}`}>
      {source}
    </span>
  );
};

const EMPTY_FORM = { label: "", slug: "", title: "", content: "", meta_description: "" };

export default function WorkspacePage() {
  const { activeSite, fetchHealth } = useSite();
  const [targets, setTargets] = useState(null);
  const [registry, setRegistry] = useState("database");
  const [loading, setLoading] = useState(false);

  // editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // target id when editing
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);

  // deploy / delete state
  const [deployBusy, setDeployBusy] = useState(null); // target id
  const [liveConfirm, setLiveConfirm] = useState(null); // target pending live confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const pollRef = useRef(null);

  // importer
  const [importUrl, setImportUrl] = useState("");
  const [importMax, setImportMax] = useState(25);
  const [importJob, setImportJob] = useState(null);
  const importPollRef = useRef(null);

  // template stamping
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState("");
  const [generating, setGenerating] = useState(false);

  const siteId = activeSite?.id;
  const isSeed = !!activeSite?.is_seed;

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sites/${siteId}/targets`);
      setTargets(data.targets || []);
      setRegistry(data.registry);
    } catch (e) {
      setTargets([]);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    setTargets(null);
    setImportJob(null);
    load();
    return () => {
      clearInterval(pollRef.current);
      clearInterval(importPollRef.current);
    };
  }, [load]);

  // ---------------- editor
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setEditorOpen(true);
  };

  const openEdit = async (t) => {
    setEditing(t.id);
    setForm({ label: t.label, slug: t.slug, title: t.title, content: "", meta_description: t.meta_description || "" });
    setEditorOpen(true);
    setLoadingContent(true);
    try {
      const { data } = await axios.get(`${API}/sites/${siteId}/targets/${t.id}`);
      setForm((f) => ({ ...f, content: data.content || "" }));
    } catch (e) {
      toast.error("Could not load page content.");
    } finally {
      setLoadingContent(false);
    }
  };

  const saveTarget = async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/sites/${siteId}/targets/${editing}`, form);
        toast.success("Page saved.");
      } else {
        await axios.post(`${API}/sites/${siteId}/targets`, form);
        toast.success(`Page "${form.label}" added to the workspace.`);
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the page.");
    } finally {
      setSaving(false);
    }
  };

  const removeTarget = async (t) => {
    setDeleteConfirm(null);
    try {
      await axios.delete(`${API}/sites/${siteId}/targets/${t.id}`);
      toast.success(`"${t.label}" removed from the page list.`);
      load();
    } catch (e) {
      toast.error("Could not delete the page.");
    }
  };

  // ---------------- deploys
  const deploy = async (t, mode) => {
    setLiveConfirm(null);
    setDeployBusy(t.id);
    try {
      const { data } = await axios.post(`${API}/wp/deploy`, {
        target: isSeed ? t.id : t.id,
        mode,
        confirm: mode === "live",
        site_id: siteId,
      });
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: job } = await axios.get(`${API}/wp/deploy/${data.job_id}`);
          if (job.status === "completed" || job.status === "failed") {
            clearInterval(pollRef.current);
            setDeployBusy(null);
            fetchHealth(siteId, true);
            load();
            if (job.status === "completed") {
              const link = job.result?.live_link || job.result?.preview_link;
              toast.success(`"${t.label}" deployed (${mode}).`, {
                action: link ? { label: mode === "live" ? "View" : "Preview", onClick: () => window.open(link, "_blank", "noopener") } : undefined,
              });
            } else {
              toast.error(`Deployment of "${t.label}" failed — see the Deploy Engine log.`);
            }
          }
        } catch (err) {
          /* keep polling */
        }
      }, 2000);
    } catch (e) {
      setDeployBusy(null);
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start the deployment.");
    }
  };

  // ---------------- importer
  const startImport = async () => {
    try {
      const { data } = await axios.post(`${API}/sites/${siteId}/scrape`, {
        source_url: importUrl,
        max_pages: Number(importMax) || 25,
      });
      setImportJob({ id: data.job_id, status: "queued", steps: [] });
      clearInterval(importPollRef.current);
      importPollRef.current = setInterval(async () => {
        try {
          const { data: job } = await axios.get(`${API}/sites/${siteId}/scrape/${data.job_id}`);
          setImportJob(job);
          if (job.status === "completed" || job.status === "failed") {
            clearInterval(importPollRef.current);
            load();
            if (job.status === "completed") {
              toast.success(`Import finished — ${job.result?.imported ?? 0} pages added${job.result?.duplicates_flagged ? `, ${job.result.duplicates_flagged} duplicate-copy pages flagged` : ""}.`);
            } else {
              toast.error("Import failed — see the log below.");
            }
          }
        } catch (err) {
          /* keep polling */
        }
      }, 2000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start the import.");
    }
  };

  const importRunning = importJob && (importJob.status === "queued" || importJob.status === "running");

  // ---------------- template stamping
  useEffect(() => {
    axios.get(`${API}/templates`)
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  const activeTemplate = templates.find((t) => t.key === templateKey);

  const generateFromTemplate = async () => {
    if (!templateKey) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/sites/${siteId}/generate`, { template: templateKey });
      toast.success(
        `"${data.label}" stamped — ${data.created} pages added, ${data.updated} refreshed (${data.total} total).`,
        data.demo_route ? {
          action: { label: "Open Live Demo", onClick: () => window.open(data.demo_route, "_blank", "noopener") },
          duration: 8000,
        } : { duration: 6000 },
      );
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not generate the template pages.");
    } finally {
      setGenerating(false);
    }
  };

  if (!activeSite) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0050c0]" />
      </div>
    );
  }

  return (
    <div data-testid="workspace-page" className="min-h-screen bg-[#fafafa] pb-20">
      <div className="mx-auto max-w-5xl px-4 pt-8">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Globe2 size={20} className="text-[#0050c0]" />
            <h1 className="font-nunito text-2xl font-extrabold text-[#383838]">Workspace Pages</h1>
          </div>
          <Badge data-testid="workspace-registry-chip" className={`rounded-[6px] border font-nunito text-[9px] font-extrabold uppercase tracking-[0.12em] px-2 ${registry === "compiled" ? "bg-[#fdf7e1] text-[#8a6d00] border-[#f0e3ae] hover:bg-[#fdf7e1]" : "bg-[#e9f1fd] text-[#0050c0] border-[#c8dcf7] hover:bg-[#e9f1fd]"}`}>
            {registry === "compiled" ? "Compiled mockups" : "Database registry"}
          </Badge>
          <span className="text-sm text-[#6e6e6e]">{activeSite.name} · {activeSite.production_domain}</span>
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" data-testid="workspace-refresh-button" onClick={load} className="rounded-[10px]">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </Button>
            {!isSeed && (
              <Button type="button" size="sm" data-testid="workspace-add-page-button" onClick={openCreate} className="rounded-[10px] bg-[#0050c0] hover:bg-[#0343a0] text-white font-nunito font-extrabold uppercase tracking-wide text-xs">
                <Plus size={14} /> Add Page
              </Button>
            )}
          </div>
        </div>

        {isSeed ? (
          <div data-testid="workspace-seed-banner" className="mt-4 flex items-start gap-2 rounded-[12px] border border-sky-500/25 bg-sky-500/[0.06] px-4 py-3 text-[13px] text-[#38505e]">
            <Info size={15} className="mt-0.5 shrink-0 text-sky-600" />
            <span>
              <b>{activeSite.name}</b> uses the compiled mockup registry — its {targets?.length ?? 27} pages are built from React mockups and deploy from the top bar while browsing each page. The list below is read-only.
            </span>
          </div>
        ) : (
          <>
          {/* Template stamper card */}
          <div data-testid="workspace-template-card" className="mt-5 rounded-[14px] border border-black/10 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Wand2 size={15} className="text-[#f07000]" />
              <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Generate From Template</h2>
            </div>
            <p className="mt-1 text-[12.5px] text-[#6e6e6e]">
              Stamps a complete brand page system into this workspace's page list. Re-running refreshes previously generated pages by slug — imported and manual pages are never touched.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[260px] space-y-1">
                <Label className="text-[10px] font-nunito font-bold uppercase tracking-wide text-[#6e6e6e]">Template</Label>
                <Select value={templateKey} onValueChange={setTemplateKey}>
                  <SelectTrigger data-testid="template-select-trigger" className="rounded-[10px]">
                    <SelectValue placeholder="Choose a brand template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.key} value={t.key} data-testid={`template-option-${t.key}`}>
                        <span className="flex items-center gap-2">
                          <span className="flex gap-0.5">
                            {t.brand_colors.map((c) => (
                              <span key={c} className="inline-block h-2.5 w-2.5 rounded-full border border-black/10" style={{ background: c }} />
                            ))}
                          </span>
                          {t.label} · {t.page_count} pages
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" data-testid="template-generate-button" onClick={generateFromTemplate} disabled={!templateKey || generating} className="rounded-[10px] bg-[#f07000] hover:bg-[#d96400] text-white font-nunito font-extrabold uppercase tracking-wide text-xs">
                {generating ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                {generating ? "Generating…" : "Generate Pages"}
              </Button>
              {activeTemplate?.demo_route && (
                <a href={activeTemplate.demo_route} target="_blank" rel="noopener noreferrer" data-testid="template-demo-link" className="pb-2 text-[12px] font-nunito font-extrabold text-sky-700 hover:underline">
                  Live demo →
                </a>
              )}
            </div>
            {activeTemplate && (
              <p data-testid="template-description" className="mt-2 text-[11.5px] leading-relaxed text-[#9a9a9a]">{activeTemplate.description}</p>
            )}
          </div>

          {/* Importer card */}
          <div data-testid="workspace-import-card" className="mt-5 rounded-[14px] border border-black/10 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Import size={15} className="text-[#f07000]" />
              <h2 className="font-nunito text-sm font-extrabold uppercase tracking-[0.1em] text-[#383838]">Import From Live Site</h2>
            </div>
            <p className="mt-1 text-[12.5px] text-[#6e6e6e]">
              Scrapes a live website (no admin access needed), extracts each page's copy, flags duplicate content, and fills this page list — ready to deploy into an empty WordPress shell.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[220px] space-y-1">
                <Label className="text-[10px] font-nunito font-bold uppercase tracking-wide text-[#6e6e6e]">Source URL</Label>
                <Input data-testid="import-source-url-input" placeholder="https://example-agency.com" value={importUrl} onChange={(e) => setImportUrl(e.target.value)} disabled={importRunning} />
              </div>
              <div className="w-24 space-y-1">
                <Label className="text-[10px] font-nunito font-bold uppercase tracking-wide text-[#6e6e6e]">Max pages</Label>
                <Input data-testid="import-max-pages-input" type="number" min={1} max={60} value={importMax} onChange={(e) => setImportMax(e.target.value)} disabled={importRunning} />
              </div>
              <Button type="button" data-testid="import-start-button" onClick={startImport} disabled={importRunning || importUrl.length < 5} className="rounded-[10px] bg-[#f07000] hover:bg-[#d96400] text-white font-nunito font-extrabold uppercase tracking-wide text-xs">
                {importRunning ? <Loader2 size={13} className="animate-spin" /> : <Import size={13} />}
                {importRunning ? "Importing…" : "Start Import"}
              </Button>
            </div>
            {importJob && (
              <div data-testid="import-progress-log" className="mt-3 max-h-44 overflow-y-auto rounded-[10px] bg-[#0b0b0c] p-3 font-mono text-[11px] leading-relaxed text-white/80">
                {(importJob.steps || []).map((s, i) => (
                  <div key={i} className={s.level === "error" ? "text-[#ffab7a]" : s.level === "warn" ? "text-amber-400" : s.level === "success" ? "text-[#8ab8ff]" : "text-white/60"}>
                    {s.message}
                  </div>
                ))}
                {importRunning && <div className="text-white/50">…</div>}
              </div>
            )}
          </div>
          </>
        )}

        {/* Targets table */}
        <div className="mt-5 rounded-[14px] border border-black/10 bg-white shadow-sm overflow-hidden">
          {targets === null ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-9 bg-black/10" />
              <Skeleton className="h-9 bg-black/10" />
              <Skeleton className="h-9 bg-black/10" />
            </div>
          ) : targets.length === 0 ? (
            <div data-testid="workspace-empty-state" className="flex flex-col items-center gap-2 py-14 text-center">
              <FileText size={26} className="text-black/20" />
              <p className="font-nunito text-sm font-extrabold text-[#383838]">No pages in this workspace yet</p>
              <p className="text-[12.5px] text-[#6e6e6e] max-w-sm">Import from a live site above, or add pages manually — then deploy them to this workspace's WordPress.</p>
            </div>
          ) : (
            <Table data-testid="workspace-targets-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-nunito text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8a8a8a]">Page</TableHead>
                  <TableHead className="font-nunito text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8a8a8a]">Slug</TableHead>
                  <TableHead className="font-nunito text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8a8a8a]">Source</TableHead>
                  <TableHead className="font-nunito text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8a8a8a]">Last Deploy</TableHead>
                  <TableHead className="text-right font-nunito text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8a8a8a]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {targets.map((t) => (
                  <TableRow key={t.id} data-testid={`workspace-target-row-${t.slug}`}>
                    <TableCell className="max-w-[220px]">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate font-nunito text-[13px] font-extrabold text-[#383838]">{t.label}</span>
                        {t.duplicate_of && (
                          <span data-testid="duplicate-copy-badge" title="Duplicate copy detected — same text as another imported page" className="inline-flex items-center gap-0.5 rounded-[5px] border border-amber-500/40 bg-amber-500/15 px-1 py-0.5 font-nunito text-[8px] font-extrabold uppercase tracking-[0.08em] text-amber-700">
                            <CopyX size={9} /> Dup
                          </span>
                        )}
                      </div>
                      {t.content_chars !== null && <span className="text-[10.5px] text-[#9a9a9a]">{t.content_chars.toLocaleString()} chars</span>}
                    </TableCell>
                    <TableCell className="text-[12px] text-[#6e6e6e]">/{t.slug}/</TableCell>
                    <TableCell><SourceChip source={t.source} /></TableCell>
                    <TableCell className="text-[11px] text-[#6e6e6e]">
                      {t.last_deploy ? (
                        <span>
                          <span className={t.last_deploy.status === "completed" ? "text-[#0050c0]" : "text-[#c25a00]"}>{t.last_deploy.status}</span>
                          {" · "}{t.last_deploy.mode} · {timeAgo(t.last_deploy.at)}
                        </span>
                      ) : (
                        <span className="text-[#b5b5b5]">never</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1.5">
                        {t.source_url && (
                          <a href={t.source_url} target="_blank" rel="noopener noreferrer" aria-label="View source page" data-testid="target-source-link" className="text-[#9a9a9a] hover:text-[#383838] transition-colors duration-150">
                            <ExternalLink size={13} />
                          </a>
                        )}
                        {!isSeed && (
                          <>
                            <Button type="button" variant="outline" size="sm" data-testid="target-edit-button" onClick={() => openEdit(t)} className="h-7 rounded-[8px] px-2">
                              <Pencil size={12} />
                            </Button>
                            {deployBusy === t.id ? (
                              <span className="flex items-center gap-1 px-2 text-amber-600 font-nunito text-[9px] font-extrabold uppercase tracking-[0.1em]">
                                <Loader2 size={12} className="animate-spin" /> Deploying
                              </span>
                            ) : (
                              <>
                                <Button type="button" size="sm" data-testid="target-deploy-draft-button" disabled={!!deployBusy} onClick={() => deploy(t, "draft")} className="h-7 rounded-[8px] bg-[#f07000] px-2 font-nunito text-[9px] font-extrabold uppercase tracking-[0.1em] text-white hover:bg-[#d96400]">
                                  <Rocket size={11} /> Draft
                                </Button>
                                <Button type="button" size="sm" data-testid="target-deploy-live-button" disabled={!!deployBusy} onClick={() => setLiveConfirm(t)} className="h-7 rounded-[8px] bg-[#0050c0] px-2 font-nunito text-[9px] font-extrabold uppercase tracking-[0.1em] text-white hover:bg-[#0343a0]">
                                  <Globe size={11} /> Live
                                </Button>
                              </>
                            )}
                            <Button type="button" variant="outline" size="sm" data-testid="target-delete-button" onClick={() => setDeleteConfirm(t)} className="h-7 rounded-[8px] border-[#f07000]/40 px-2 text-[#c25a00] hover:bg-[#f07000]/10">
                              <Trash2 size={12} />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent data-testid="target-editor-dialog" className="sm:max-w-[640px] rounded-[14px]">
          <DialogHeader>
            <DialogTitle className="font-nunito font-extrabold text-[#383838]">{editing ? "Edit Page" : "Add Page"}</DialogTitle>
            <DialogDescription className="text-[#6e6e6e]">
              Content is deployed as-is to WordPress (Flatsome shortcodes or HTML both work).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-nunito font-bold uppercase tracking-wide text-[#6e6e6e]">Label</Label>
                <Input data-testid="target-editor-label-input" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-nunito font-bold uppercase tracking-wide text-[#6e6e6e]">Slug</Label>
                <Input data-testid="target-editor-slug-input" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-nunito font-bold uppercase tracking-wide text-[#6e6e6e]">Page Title</Label>
              <Input data-testid="target-editor-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-nunito font-bold uppercase tracking-wide text-[#6e6e6e]">
                Content {loadingContent && <Loader2 size={10} className="ml-1 inline animate-spin" />}
              </Label>
              <Textarea data-testid="target-editor-content-input" rows={12} className="font-mono text-[12px]" value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" data-testid="target-editor-cancel-button" onClick={() => setEditorOpen(false)} className="rounded-[10px]">Cancel</Button>
            <Button type="button" data-testid="target-editor-save-button" disabled={saving || !form.label || !form.slug} onClick={saveTarget} className="rounded-[10px] bg-[#0050c0] hover:bg-[#0343a0] text-white font-nunito font-extrabold uppercase tracking-wide text-xs">
              {saving ? <Loader2 size={13} className="animate-spin" /> : null} Save Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Live deploy confirm */}
      <AlertDialog open={!!liveConfirm} onOpenChange={(o) => !o && setLiveConfirm(null)}>
        <AlertDialogContent data-testid="target-live-confirm-dialog" className="rounded-[14px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-nunito font-extrabold text-[#383838]">Publish “{liveConfirm?.label}” live?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6e6e6e]">
              This publishes /{liveConfirm?.slug}/ on {activeSite.production_domain} ({activeSite.environment}). A pre-overwrite snapshot is saved for one-tap rollback.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="target-live-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="target-live-confirm-yes" onClick={() => deploy(liveConfirm, "live")} className="bg-[#0050c0] hover:bg-[#0343a0] text-white">
              Yes, Publish Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent data-testid="target-delete-confirm-dialog" className="rounded-[14px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-nunito font-extrabold text-[#383838]">Remove “{deleteConfirm?.label}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6e6e6e]">
              Removes the page from this workspace's list. Any pages already deployed to WordPress are not touched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="target-delete-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="target-delete-confirm-yes" onClick={() => removeTarget(deleteConfirm)} className="bg-[#c2410c] hover:bg-[#9a3412] text-white">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
